/**
 * Otomatik kese ürünü sepete ekleme/çıkarma kontrolü.
 * api-bag-calculate section'ından gelen verilere göre kese ürününü yönetir.
 *
 * Kese kurgusu tamamen storefront JS'te yürüdüğünden iki ek güvence içerir:
 *
 * 1. **Her sayfada kontrol + yakınsama**: Sayfa yüklenişinde (ve bfcache
 *    dönüşünde) sepet durumu doğrulanır; her operasyondan sonra taze section
 *    verisiyle tekrar kontrol edilir, durum tutarlı olana kadar yakınsar.
 * 2. **Checkout kilidi**: Kese operasyonu sürerken sepet sayfasındaki ödeme
 *    butonları disable edilir ve cart formunun submit'i (capture fazında)
 *    engellenir; müşteri kese ekleme/çıkarma tamamlanmadan ödemeye geçemez.
 *    Sepet re-render'ları (innerHTML değişimi) butonu sıfırladığından state
 *    MutationObserver ile yeniden uygulanır.
 *
 * @class HPAutoBagControl
 */
class HPAutoBagControl {
  /** @type {string} */
  static SECTION_ID = 'api-bag-calculate';

  /** @type {string} */
  static DATA_ELEMENT_ID = 'api-bag-calculate-data';

  /** @type {string} */
  static EVENT_NAME = 'cart:bagCalculate';

  /** Kese operasyonları başladığında tetiklenen event. */
  static OPS_START_EVENT = 'cart:bagOperationsStart';

  /** Tüm kese operasyonları bittiğinde tetiklenen event. */
  static OPS_END_EVENT = 'cart:bagOperationsEnd';

  /** Yalnızca bizim disable ettiğimiz checkout butonlarını işaretleyen class. */
  static GUARD_CLASS = 'kese-sync-disabled';

  /** Ek ödeme butonu (Shop Pay vb.) sarmalayıcılarını kilitleyen class. */
  static GUARD_WRAPPER_CLASS = 'kese-syncing';

  /** Operasyon takılı kalırsa fail-open süresi (ms) — checkout kilidi asla kalıcı olmasın. */
  static FAILSAFE_TIMEOUT_MS = 15000;

  /** Flapping koruması: OP_WINDOW_MS içinde en fazla MAX_OPS_PER_WINDOW operasyon. */
  static OP_WINDOW_MS = 10000;

  /** @type {number} */
  static MAX_OPS_PER_WINDOW = 6;

  /**
   * @type {{pendingOps: number, opInFlight: boolean, recheckQueued: boolean,
   *         failsafeTimer: ReturnType<typeof setTimeout>|null, opTimestamps: number[]}}
   */
  static state = {
    pendingOps: 0,
    opInFlight: false,
    recheckQueued: false,
    failsafeTimer: null,
    opTimestamps: [],
  };

  /**
   * Şu an bekleyen kese operasyonu var mı? (Checkout kilidi bu duruma bağlıdır.)
   *
   * @returns {boolean}
   */
  static isBusy() {
    return HPAutoBagControl.state.pendingOps > 0;
  }

  /**
   * api-bag-calculate section HTML'inden JSON verisini parse eder.
   *
   * @param {string} sectionHtml - response.sections['api-bag-calculate'] HTML string
   * @returns {Object|null} Parse edilmiş bag verisi veya null
   */
  static parseBagCalculateData(sectionHtml) {
    if (!sectionHtml || typeof sectionHtml !== 'string') return null;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHtml, 'text/html');
      const scriptEl = doc.getElementById(HPAutoBagControl.DATA_ELEMENT_ID);
      if (!scriptEl?.textContent) return null;
      return JSON.parse(scriptEl.textContent.trim());
    } catch (e) {
      console.warn('hp-auto-bag-control: Failed to parse bag calculate data', e);
      return null;
    }
  }

  /**
   * Section Rendering API ile api-bag-calculate section'ını cart context'inde çeker.
   *
   * @returns {Promise<string|null>} Sayfa HTML (section içerir) veya null
   */
  static async fetchSectionHtml() {
    const cartUrl = window.routes?.cart_url || '/cart';
    const url = `${cartUrl}?section_id=${HPAutoBagControl.SECTION_ID}`;
    try {
      const res = await fetch(url);
      return res.ok ? res.text() : null;
    } catch (e) {
      console.warn('hp-auto-bag-control: Failed to fetch section', e);
      return null;
    }
  }

  /**
   * Section HTML'den bag verisini alır. Önce response'tan, yoksa Section Rendering API ile.
   *
   * @param {Object} response - Cart API response (sections key'i olabilir)
   * @returns {Promise<Object|null>} Bag verisi veya null
   */
  static async getBagDataFromResponse(response) {
    let sectionHtml = response?.sections?.[HPAutoBagControl.SECTION_ID];

    if (!sectionHtml) {
      sectionHtml = await HPAutoBagControl.fetchSectionHtml();
    }

    return sectionHtml ? HPAutoBagControl.parseBagCalculateData(sectionHtml) : null;
  }

  /**
   * Cart API response'unda api-bag-calculate varsa event tetikler.
   * Yoksa Section Rendering API ile section çekip event tetikler.
   *
   * @param {Object} response - Cart API response (sections key'i olabilir)
   */
  static async dispatchBagCalculateIfNeeded(response) {
    const bagData = await HPAutoBagControl.getBagDataFromResponse(response);
    if (!bagData) {
      // Veri alınamadıysa checkout kilidini asılı bırakma (fail-open).
      HPAutoBagControl.syncCheckoutGuards();
      return;
    }

    document.dispatchEvent(
      new CustomEvent(HPAutoBagControl.EVENT_NAME, { detail: bagData })
    );
  }

  /**
   * Operasyon başlangıcı: sayaç artar, ilk operasyonda checkout kilidi devreye
   * girer ve fail-open zamanlayıcısı kurulur.
   */
  static beginOp() {
    const state = HPAutoBagControl.state;
    state.pendingOps += 1;
    if (state.pendingOps === 1) {
      document.dispatchEvent(new CustomEvent(HPAutoBagControl.OPS_START_EVENT));
      HPAutoBagControl.syncCheckoutGuards();
      clearTimeout(state.failsafeTimer);
      state.failsafeTimer = setTimeout(() => {
        console.warn('hp-auto-bag-control: Operation stuck, releasing checkout guard');
        state.pendingOps = 0;
        state.opInFlight = false;
        state.recheckQueued = false;
        document.dispatchEvent(new CustomEvent(HPAutoBagControl.OPS_END_EVENT));
        HPAutoBagControl.syncCheckoutGuards();
      }, HPAutoBagControl.FAILSAFE_TIMEOUT_MS);
    }
  }

  /**
   * Operasyon bitişi: sayaç azalır, sıfırlanınca checkout kilidi açılır.
   */
  static endOp() {
    const state = HPAutoBagControl.state;
    state.pendingOps = Math.max(0, state.pendingOps - 1);
    if (state.pendingOps === 0) {
      clearTimeout(state.failsafeTimer);
      state.failsafeTimer = null;
      document.dispatchEvent(new CustomEvent(HPAutoBagControl.OPS_END_EVENT));
      HPAutoBagControl.syncCheckoutGuards();
    }
  }

  /**
   * Flapping koruması: kısa aralıkta çok fazla operasyon dönüyorsa (ör. section
   * hesabı ile cart durumu arasında kararsızlık) yeni operasyon başlatma.
   *
   * @returns {boolean} Yeni operasyona izin var mı
   */
  static allowNewOp() {
    const state = HPAutoBagControl.state;
    const now = Date.now();
    state.opTimestamps = state.opTimestamps.filter(
      (t) => now - t < HPAutoBagControl.OP_WINDOW_MS
    );
    if (state.opTimestamps.length >= HPAutoBagControl.MAX_OPS_PER_WINDOW) {
      console.warn('hp-auto-bag-control: Too many bag operations, backing off');
      return false;
    }
    state.opTimestamps.push(now);
    return true;
  }

  /**
   * Tek bir kese operasyonunu busy-state takibi ile çalıştırır. Operasyon
   * bittikten sonra taze veri (operasyon response'ından ya da kuyruklanan
   * re-check'ten) tekrar dispatch edilir; böylece durum yakınsayana kadar
   * kontrol sürer.
   *
   * @param {() => Promise<Object|null>} opFn - Taze bag verisi döndüren operasyon
   * @returns {Promise<void>}
   */
  static async runBagOperation(opFn) {
    const state = HPAutoBagControl.state;
    state.opInFlight = true;
    HPAutoBagControl.beginOp();

    let nextBagData = null;
    try {
      nextBagData = await opFn();
    } catch (err) {
      console.error('hp-auto-bag-control: Bag operation failed', err);
    } finally {
      state.opInFlight = false;
      HPAutoBagControl.endOp();
    }

    // Operasyon sürerken yeni bir hesap isteği geldiyse taze section çek.
    if (state.recheckQueued) {
      state.recheckQueued = false;
      nextBagData = await HPAutoBagControl.getBagDataFromResponse({});
    }

    if (nextBagData) {
      document.dispatchEvent(
        new CustomEvent(HPAutoBagControl.EVENT_NAME, { detail: nextBagData })
      );
    }
  }

  /**
   * Kese ürününü sepete ekler ve response'taki taze section verisini döndürür.
   *
   * @param {number} bagVariantId - Kese ürünü variant ID
   * @returns {Promise<Object|null>} Taze bag verisi veya null
   */
  static async addBagToCart(bagVariantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        items: [{ id: bagVariantId, quantity: 1 }],
        sections: HPAutoBagControl.SECTION_ID,
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) {
      console.warn('hp-auto-bag-control: Failed to add bag, status', res.status);
      return null;
    }
    const data = await res.json().catch(() => null);
    document.dispatchEvent(new CustomEvent('cart:updated'));
    return data?.sections
      ? HPAutoBagControl.parseBagCalculateData(data.sections[HPAutoBagControl.SECTION_ID])
      : null;
  }

  /**
   * Sepetteki kese ürününün miktarını günceller (0 = sil, 1 = azalt) ve
   * response'taki taze section verisini döndürür. Satır hedeflemede öncelik
   * line item key'dedir; index (line) cart bu arada değiştiyse yanlış satırı
   * gösterebilir.
   *
   * @param {number} bagLine - Kese ürününün sepetteki 1-based satır indexi
   * @param {number} quantity - Yeni miktar (0 veya 1)
   * @param {string|null} bagLineKey - Kese satırının line item key'i (varsa tercih edilir)
   * @returns {Promise<Object|null>} Taze bag verisi veya null
   */
  static async changeBagQuantity(bagLine, quantity, bagLineKey) {
    const target = bagLineKey ? { id: bagLineKey } : { line: bagLine };
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        ...target,
        quantity,
        sections: HPAutoBagControl.SECTION_ID,
        sections_url: window.location.pathname,
      }),
    });
    if (!res.ok) {
      console.warn('hp-auto-bag-control: Failed to change bag quantity, status', res.status);
      return null;
    }
    const data = await res.json().catch(() => null);
    document.dispatchEvent(new CustomEvent('cart:updated'));
    return data?.sections
      ? HPAutoBagControl.parseBagCalculateData(data.sections[HPAutoBagControl.SECTION_ID])
      : null;
  }

  /**
   * cart:bagCalculate event'ini işler ve gerekli sepete ekleme/çıkarma işlemini yapar.
   * Bir operasyon zaten sürüyorsa yeni hesap kuyruklanır (duplicate ekleme önlenir).
   *
   * @param {CustomEvent} event - cart:bagCalculate event
   */
  static handleBagCalculate(event) {
    const {
      bag_variant_id,
      should_add_bag,
      should_remove_bag,
      should_reduce_bag,
      bag_line,
      bag_line_key,
    } = event.detail;

    if (!bag_variant_id) {
      HPAutoBagControl.syncCheckoutGuards();
      return;
    }

    const state = HPAutoBagControl.state;
    const needsOp = should_add_bag || ((should_remove_bag || should_reduce_bag) && bag_line);

    if (needsOp && state.opInFlight) {
      // Süren operasyon bitince taze veriyle tekrar bakılacak.
      state.recheckQueued = true;
      return;
    }

    if (needsOp && !HPAutoBagControl.allowNewOp()) {
      HPAutoBagControl.syncCheckoutGuards();
      return;
    }

    if (should_add_bag) {
      HPAutoBagControl.runBagOperation(() =>
        HPAutoBagControl.addBagToCart(bag_variant_id)
      );
      return;
    }

    if ((should_remove_bag || should_reduce_bag) && bag_line) {
      const quantity = should_remove_bag ? 0 : 1;
      HPAutoBagControl.runBagOperation(() =>
        HPAutoBagControl.changeBagQuantity(bag_line, quantity, bag_line_key || null)
      );
      return;
    }

    // Yapılacak işlem yok: sunucu render'ından kalan parse-time kilidi de
    // dahil olmak üzere checkout butonlarını serbest bırak.
    HPAutoBagControl.syncCheckoutGuards();
  }

  /**
   * Checkout butonlarının disabled durumunu busy-state ile eşitler.
   * Yalnızca GUARD_CLASS ile bizim işaretlediğimiz butonlara dokunur; başka
   * bir özelliğin disable ettiği butonu asla yeniden aktive etmez.
   */
  static syncCheckoutGuards() {
    const busy = HPAutoBagControl.isBusy();

    document.querySelectorAll('button[name="checkout"]').forEach((btn) => {
      if (busy) {
        btn.disabled = true;
        btn.setAttribute('aria-busy', 'true');
        btn.classList.add(HPAutoBagControl.GUARD_CLASS);
      } else if (btn.classList.contains(HPAutoBagControl.GUARD_CLASS)) {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.classList.remove(HPAutoBagControl.GUARD_CLASS);
      }
    });

    document
      .querySelectorAll('.additional-checkout-buttons--vertical')
      .forEach((el) => el.classList.toggle(HPAutoBagControl.GUARD_WRAPPER_CLASS, busy));
  }

  /**
   * Kese operasyonu sürerken cart formunun submit edilmesini (Enter dahil,
   * hangi buton üzerinden gelirse gelsin) capture fazında engeller. Disabled
   * attribute'u re-render ile silinse bile bu kilit çalışır.
   *
   * @param {SubmitEvent} event
   */
  static handleGuardedSubmit(event) {
    if (!HPAutoBagControl.isBusy()) return;
    const form = event.target;
    if (form && (form.id === 'cart' || form.hasAttribute?.('data-cart-form'))) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  /**
   * Sepet sayfasındaki re-render'lar (#main-cart innerHTML değişimi) buton
   * state'ini sıfırlar; observer her değişimden sonra kilidi yeniden uygular.
   */
  static observeCartRerenders() {
    const mainCart = document.getElementById('main-cart');
    if (!mainCart || typeof MutationObserver === 'undefined') return;

    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        HPAutoBagControl.syncCheckoutGuards();
      });
    });
    observer.observe(mainCart, { childList: true, subtree: true });
  }

  /**
   * Sayfa yüklendiğinde bir kez api-bag-calculate render edip kontrol sağlar.
   * Sepette fazla/eksik kese varsa düzeltir.
   */
  static async runInitialCheck() {
    const bagData = await HPAutoBagControl.getBagDataFromResponse({});
    if (!bagData) {
      // Kontrol yapılamadı: sunucu render'ından kalan kilidi asılı bırakma.
      HPAutoBagControl.syncCheckoutGuards();
      return;
    }

    document.dispatchEvent(
      new CustomEvent(HPAutoBagControl.EVENT_NAME, { detail: bagData })
    );
  }

  /**
   * Event listener'ları kaydeder ve ilk kontrolü çalıştırır.
   */
  static init() {
    document.addEventListener(
      HPAutoBagControl.EVENT_NAME,
      HPAutoBagControl.handleBagCalculate
    );

    // Checkout kilidi: form submit'i capture fazında engelle.
    document.addEventListener('submit', HPAutoBagControl.handleGuardedSubmit, true);

    // bfcache'den dönüşte (ör. checkout'tan geri tuşu) durumu tekrar doğrula.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        HPAutoBagControl.runInitialCheck().catch((err) =>
          console.warn('hp-auto-bag-control: pageshow check failed', err)
        );
      }
    });

    HPAutoBagControl.observeCartRerenders();

    HPAutoBagControl.runInitialCheck().catch((err) => {
      console.warn('hp-auto-bag-control: Initial check failed', err);
      HPAutoBagControl.syncCheckoutGuards();
    });
  }
}

// Global erişim için window'a bağla
window.HPAutoBagControl = HPAutoBagControl;
window.dispatchBagCalculateIfNeeded = HPAutoBagControl.dispatchBagCalculateIfNeeded.bind(
  HPAutoBagControl
);

// Sayfa yüklendiğinde event listener'ı aktif et
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => HPAutoBagControl.init());
} else {
  HPAutoBagControl.init();
}
